import DashboardLayout from 'layouts/dashboard-layout/DashboardLayout';
import SettingsSidebar from 'layouts/dashboard-layout/sidebars/settings-sidebar/SettingsSidebar';
import { _t } from 'lang';
import { useNavigate, useParams } from 'react-router-dom';
import { TEAMS_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import { showNotification } from '@mantine/notifications';
import { useApi } from 'api/ApiContext';
import { useSettingsSidebar } from 'layouts/dashboard-layout/sidebars/settings-sidebar/SettingsSidebarProvider';
import { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import PageHeading from 'layouts/dashboard-layout/headers/PageHeading';
import TeamForm from 'components/forms/team-form/TeamForm';
import panic from 'errors/Panic';
import { NotFoundPageContent } from 'pages/system/not-found/NotFoundPage';

/**
 * The content of the EditTeamPage.
 *
 * @param {{ teamId: string }}
 */
function EditTeamImpl({ teamId }) {
  const navigate = useNavigate();
  const { getAction } = useApi();
  const { refreshTeams } = useSettingsSidebar();

  const [loading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState(null);

  useEffect(() => {
    const teamGetListAction = getAction('TeamGetListAction');

    setLoading(true);
    teamGetListAction({
      query: {
        filter: { 'team_id.eq': teamId },
        limit: 1,
      },
    })
      .then((data) => {
        if (data.length === 0) {
          setInitialValues({});
          return;
        }

        const team = data[0];

        const rawMembers = team.members.map((member) => String(member.user_id));

        const initialValues = {
          avatar: team.avatar,
          teamName: team.name,
          color: `#${team.color}`,
          departmentId: String(team.department.department_id),
          teamLeaderId: team.leader_id ? String(team.leader_id) : undefined,
          members: rawMembers,
        };

        setInitialValues(initialValues);
      })
      .catch(panic)
      .finally(() => {
        setLoading(false);
      });
  }, [getAction, teamId]);

  if (!teamId || (!loading && isEmpty(initialValues))) {
    return <NotFoundPageContent />;
  }

  /**
   * Sends the request to the API and edits the team.
   *
   * @param {typeof initialValues}
   */
  const submit = ({ teamName, color, departmentId, teamLeaderId }) => {
    const teamUpdateAction = getAction('TeamUpdateAction');

    return teamUpdateAction({
      parameters: { team_id: teamId },
      body: {
        name: teamName,
        color: color.replace(/^#/, ''),
        department_id: departmentId,
        leader_id: teamLeaderId ? teamLeaderId : undefined,
      },
    })
      .then(() => {
        showNotification({
          title: _t('Team updated'),
          message: _t("Team '%s' has been updated successfully.", `${teamName}`),
          color: SUCCESS_NOTIFICATION_COLOR,
        });

        refreshTeams();
      })
      .catch((e) => {
        panic(e);

        navigate(TEAMS_PAGE_PATH.original);
      });
  };

  return (
    <>
      <PageHeading heading={_t('Edit team')} />
      {!loading && <TeamForm initialValues={initialValues} onSubmit={submit} />}
    </>
  );
}

/**
 * Displays a form to edit a team.
 *
 * @see https://www.figma.com/file/X3Zklb3KjOZFu5wAHgGYoH/Toolio---web-Interface?node-id=508%3A25807
 */
export default function EditTeamPage() {
  const { teamId } = useParams();

  return (
    <DashboardLayout sidebar={<SettingsSidebar />}>
      <div className="min-h-[calc(100vh-60px)] bg-legacy-elements-body p-8 pt-0">
        <EditTeamImpl key={teamId} teamId={teamId} />
      </div>
    </DashboardLayout>
  );
}
